import { z } from "zod";

/**
 * User-defined metrics: a named arithmetic formula over the row's base metrics,
 * e.g. "total_revenue / unique_global". Evaluated with a hand-written parser —
 * NEVER eval/Function — so a stored formula can't run arbitrary code.
 *
 * The parser/evaluator and the storage shaping are pure (no browser), so they
 * unit-test on their own; load/persist are the localStorage edges.
 */
export type CustomMetricFormat = "int" | "currency" | "rate" | "percent";

export interface CustomMetric {
  id: string;
  name: string;
  formula: string;
  format: CustomMetricFormat;
}

export const CUSTOM_METRICS_KEY = "oracul.clicks.customMetrics.v1";

// ---- formula engine (tokenizer + recursive-descent, no eval) ----------------

type Token =
  | { t: "num"; v: number }
  | { t: "id"; v: string }
  | { t: "op"; v: "+" | "-" | "*" | "/" }
  | { t: "("; v: "(" }
  | { t: ")"; v: ")" };

const tokenize = (input: string): Token[] => {
  const tokens: Token[] = [];
  let i = 0;
  while (i < input.length) {
    const c = input[i];
    if (/\s/.test(c)) {
      i++;
    } else if (/[0-9.]/.test(c)) {
      let j = i;
      while (j < input.length && /[0-9.]/.test(input[j])) j++;
      const num = Number(input.slice(i, j));
      if (!Number.isFinite(num)) throw new Error(`Bad number: ${input.slice(i, j)}`);
      tokens.push({ t: "num", v: num });
      i = j;
    } else if (/[a-zA-Z_]/.test(c)) {
      let j = i;
      while (j < input.length && /[a-zA-Z0-9_]/.test(input[j])) j++;
      tokens.push({ t: "id", v: input.slice(i, j) });
      i = j;
    } else if (c === "+" || c === "-" || c === "*" || c === "/") {
      tokens.push({ t: "op", v: c });
      i++;
    } else if (c === "(") {
      tokens.push({ t: "(", v: "(" });
      i++;
    } else if (c === ")") {
      tokens.push({ t: ")", v: ")" });
      i++;
    } else {
      throw new Error(`Unexpected character: ${c}`);
    }
  }
  return tokens;
};

// Grammar: expr = term (("+"|"-") term)*; term = unary (("*"|"/") unary)*;
// unary = "-" unary | primary; primary = num | id | "(" expr ")".
const compute = (tokens: Token[], resolve: (name: string) => number): number => {
  let pos = 0;
  const peek = (): Token | undefined => tokens[pos];
  const next = (): Token | undefined => tokens[pos++];

  const parseExpr = (): number => {
    let value = parseTerm();
    for (;;) {
      const tk = peek();
      if (tk && tk.t === "op" && (tk.v === "+" || tk.v === "-")) {
        next();
        const rhs = parseTerm();
        value = tk.v === "+" ? value + rhs : value - rhs;
      } else break;
    }
    return value;
  };

  const parseTerm = (): number => {
    let value = parseUnary();
    for (;;) {
      const tk = peek();
      if (tk && tk.t === "op" && (tk.v === "*" || tk.v === "/")) {
        next();
        const rhs = parseUnary();
        value = tk.v === "*" ? value * rhs : value / rhs;
      } else break;
    }
    return value;
  };

  const parseUnary = (): number => {
    const tk = peek();
    if (tk && tk.t === "op" && tk.v === "-") {
      next();
      return -parseUnary();
    }
    if (tk && tk.t === "op" && tk.v === "+") {
      next();
      return parseUnary();
    }
    return parsePrimary();
  };

  const parsePrimary = (): number => {
    const tk = next();
    if (!tk) throw new Error("Unexpected end of formula");
    if (tk.t === "num") return tk.v;
    if (tk.t === "id") return resolve(tk.v);
    if (tk.t === "(") {
      const value = parseExpr();
      const close = next();
      if (!close || close.t !== ")") throw new Error("Expected )");
      return value;
    }
    throw new Error(`Unexpected token: ${tk.v}`);
  };

  const result = parseExpr();
  if (pos !== tokens.length) throw new Error("Unexpected trailing input");
  return result;
};

/**
 * Evaluate a formula against a row's values. Missing identifiers resolve to
 * `missing` (0 by default; NaN makes the whole result null), and a non-finite
 * result (e.g. divide by zero) returns null so the cell can show "—" rather
 * than Infinity/NaN.
 */
export const evalFormula = (
  formula: string,
  vars: Record<string, number>,
  missing = 0,
): number | null => {
  try {
    const tokens = tokenize(formula);
    if (tokens.length === 0) return null;
    const value = compute(tokens, (name) => {
      const v = vars[name];
      return typeof v === "number" && Number.isFinite(v) ? v : missing;
    });
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
};

/**
 * Every custom metric's value onto each row, from the row's own numbers plus
 * `extra` (fields derived in the browser, e.g. uepc). Run it on the final
 * rows - after GSC, keyword volume and capture rate are merged in - or a
 * formula over those reads nothing. A field the row has no number for (no GSC
 * data, no volume) makes the result null, "—" on screen, not a value
 * computed with 0.
 */
export const applyCustomMetrics = <T extends object>(
  rows: T[],
  metrics: CustomMetric[],
  extra?: (row: T) => Record<string, number>,
): T[] => {
  if (metrics.length === 0) return rows;
  return rows.map((row) => {
    const vars: Record<string, number> = {};
    for (const [k, v] of Object.entries(row)) if (typeof v === "number") vars[k] = v;
    Object.assign(vars, extra?.(row));
    const values: Record<string, number | null> = {};
    for (const m of metrics) values[m.id] = evalFormula(m.formula, vars, NaN);
    return { ...row, ...values };
  });
};

/** Check a formula is safe and only references allowed metric names. */
export const validateFormula = (
  formula: string,
  allowed: Set<string>,
): { ok: true } | { ok: false; error: string } => {
  const trimmed = formula.trim();
  if (!trimmed) return { ok: false, error: "Formula is empty" };

  let tokens: Token[];
  try {
    tokens = tokenize(trimmed);
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }

  const unknown = tokens.find((t) => t.t === "id" && !allowed.has(t.v));
  if (unknown && unknown.t === "id")
    return { ok: false, error: `Unknown metric: ${unknown.v}` };

  try {
    compute(tokens, () => 1); // structural check with dummy values
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
  return { ok: true };
};

// ---- format inference (dimensional analysis) --------------------------------

/**
 * The "unit" of a value, tracked as exponents of two base dimensions — MONEY
 * and COUNT — plus a flag for percentages (which are dimensionless but shown
 * with a % sign). Revenue is money {m:1,c:0}; clicks are a count {m:0,c:1};
 * EPC is money-per-click {m:1,c:-1}; CR% is a dimensionless percentage.
 */
type Unit = { m: number; c: number; pct: boolean };
/** A node's unit plus its literal value when it is a bare number (for the ×100 rule). */
type Typed = { u: Unit; lit: number | null };

const COUNT_FIELDS = new Set([
  "total_clicks", "unique_campaign", "unique_stream", "unique_global", "lp_clicks",
  "total_conversions", "total_leads", "total_sales",
  "total_bots", "bots_oracul", "not_bots",
  "bots_ua", "bots_isp", "bots_device", "bots_ip_burst", "bots_ip_visitors",
  "bots_language", "bots_no_browser", "bots_old_chrome", "bots_proxy",
  "gsc_clicks", "gsc_impressions", "kv_last", "kv_avg",
]);
const MONEY_FIELDS = new Set(["total_revenue", "total_cost", "total_profit"]);
const MONEY_RATE_FIELDS = new Set([
  "epc", "uepc", "ecpm", "epc_confirmed", "ecpm_confirmed", "cpc", "ecpc",
]);
const PERCENT_FIELDS = new Set([
  "lp_ctr_percent", "cr_percent", "cr_leads_percent", "cr_sales_percent",
  "bot_share_percent", "bot_share_oracul_percent", "gsc_ctr", "capture_rate",
  "kv_trend_percent", "roi_percent", "roi_confirmed_percent",
]);

const fieldUnit = (id: string): Unit => {
  if (COUNT_FIELDS.has(id) || /^kv_m\d+$/.test(id)) return { m: 0, c: 1, pct: false };
  if (MONEY_FIELDS.has(id)) return { m: 1, c: 0, pct: false };
  if (MONEY_RATE_FIELDS.has(id)) return { m: 1, c: -1, pct: false };
  if (PERCENT_FIELDS.has(id)) return { m: 0, c: 0, pct: true };
  return { m: 0, c: 0, pct: false }; // gsc_position and anything unclassified: plain number
};

const dimensionless = (u: Unit) => u.m === 0 && u.c === 0;

const unitToFormat = (u: Unit): CustomMetricFormat => {
  if (u.m === 1 && u.c === 0) return "currency"; // money
  if (u.m >= 1 && u.c < 0) return "rate"; // money per unit (EPC/eCPM-like)
  if (u.m >= 1) return "currency"; // other money-ish → money
  if (u.m < 0) return "rate"; // per-money (rare) → decimals
  // m === 0 from here (no money dimension):
  if (u.pct) return "percent";
  return "int"; // counts and dimensionless ratios → plain number
};

/**
 * Infer how a formula's result should be shown, from the units of the fields
 * it combines — so the user never picks a format and can't mislabel it. Rules:
 * money/count → rate; money−money → money; count+count → count; a dimensionless
 * ratio ×100 (or any percent field) → percent. Unclassifiable or malformed →
 * "int" (a plain number), which is the harmless default.
 */
export const inferFormat = (
  formula: string,
  resolve: (id: string) => Unit = fieldUnit,
): CustomMetricFormat => {
  let tokens: Token[];
  try {
    tokens = tokenize(formula);
  } catch {
    return "int";
  }
  if (tokens.length === 0) return "int";

  let pos = 0;
  const peek = () => tokens[pos];
  const next = () => tokens[pos++];

  const parseExpr = (): Typed => {
    let left = parseTerm();
    for (;;) {
      const tk = peek();
      if (tk && tk.t === "op" && (tk.v === "+" || tk.v === "-")) {
        next();
        const right = parseTerm();
        // Adding/subtracting keeps the unit when both sides match; mixing units
        // (money + count) is nonsense, so fall back to a plain number.
        const same = left.u.m === right.u.m && left.u.c === right.u.c;
        left = {
          u: same
            ? { m: left.u.m, c: left.u.c, pct: left.u.pct || right.u.pct }
            : { m: 0, c: 0, pct: false },
          lit:
            left.lit !== null && right.lit !== null
              ? tk.v === "+" ? left.lit + right.lit : left.lit - right.lit
              : null,
        };
      } else break;
    }
    return left;
  };

  const parseTerm = (): Typed => {
    let left = parseUnary();
    for (;;) {
      const tk = peek();
      if (tk && tk.t === "op" && (tk.v === "*" || tk.v === "/")) {
        next();
        const right = parseUnary();
        const m = tk.v === "*" ? left.u.m + right.u.m : left.u.m - right.u.m;
        const c = tk.v === "*" ? left.u.c + right.u.c : left.u.c - right.u.c;
        let pct = left.u.pct || right.u.pct;
        // "× 100" turns a dimensionless ratio into a percentage (CR, CTR…).
        if (
          tk.v === "*" &&
          ((left.lit === 100 && dimensionless(right.u) && !right.u.pct) ||
            (right.lit === 100 && dimensionless(left.u) && !left.u.pct))
        )
          pct = true;
        left = {
          u: { m, c, pct },
          lit:
            left.lit !== null && right.lit !== null
              ? tk.v === "*" ? left.lit * right.lit : left.lit / right.lit
              : null,
        };
      } else break;
    }
    return left;
  };

  const parseUnary = (): Typed => {
    const tk = peek();
    if (tk && tk.t === "op" && tk.v === "-") {
      next();
      const a = parseUnary();
      return { u: a.u, lit: a.lit === null ? null : -a.lit };
    }
    if (tk && tk.t === "op" && tk.v === "+") {
      next();
      return parseUnary();
    }
    return parsePrimary();
  };

  const parsePrimary = (): Typed => {
    const tk = next();
    if (!tk) throw new Error("end");
    if (tk.t === "num") return { u: { m: 0, c: 0, pct: false }, lit: tk.v };
    if (tk.t === "id") return { u: resolve(tk.v), lit: null };
    if (tk.t === "(") {
      const inner = parseExpr();
      const close = next();
      if (!close || close.t !== ")") throw new Error(")");
      return inner;
    }
    throw new Error("token");
  };

  try {
    const result = parseExpr();
    if (pos !== tokens.length) return "int";
    return unitToFormat(result.u);
  } catch {
    return "int";
  }
};

// ---- storage shaping (pure) + localStorage edges ----------------------------

const customMetricSchema = z.object({
  id: z.string(),
  name: z.string(),
  formula: z.string(),
  format: z.enum(["int", "currency", "rate", "percent"]),
});

export const addCustomMetric = (
  list: CustomMetric[],
  metric: CustomMetric,
): CustomMetric[] => {
  const index = list.findIndex((m) => m.id === metric.id);
  if (index === -1) return [...list, metric];
  const next = [...list];
  next[index] = metric;
  return next;
};

export const removeCustomMetric = (
  list: CustomMetric[],
  id: string,
): CustomMetric[] => list.filter((m) => m.id !== id);

export const parseCustomMetrics = (raw: string | null): CustomMetric[] => {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const out: CustomMetric[] = [];
  for (const item of parsed) {
    const result = customMetricSchema.safeParse(item);
    if (result.success) out.push(result.data);
  }
  return out;
};

export const serializeCustomMetrics = (list: CustomMetric[]): string =>
  JSON.stringify(list);

export const loadCustomMetrics = (): CustomMetric[] => {
  try {
    return parseCustomMetrics(localStorage.getItem(CUSTOM_METRICS_KEY));
  } catch {
    return [];
  }
};

export const persistCustomMetrics = (list: CustomMetric[]): CustomMetric[] => {
  try {
    localStorage.setItem(CUSTOM_METRICS_KEY, serializeCustomMetrics(list));
  } catch {
    // storage unavailable — list still works for this session
  }
  return list;
};
